import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";

import { failInstallationMutation } from "@/api/queries";
import { Alert } from "@/components/ui/alert";
import { TextField } from "@/components/ui/field";
import { FormDialog } from "@/components/ui/form-dialog";
import { useApiClient } from "@/lib/api-context";

const failFormFields = z.object({
  failureReason: z.string().trim().min(10, "Say what went wrong in at least a few words.").max(500),
});

type FailFormValues = z.infer<typeof failFormFields>;

/**
 * Abandoning an installation, which is the only ending a person can declare.
 *
 * Its opposite is unavailable by design: a device is operational when a
 * reading has arrived through one of its gateways, and no form can assert
 * that (ADR-062). The reason is required because "it did not work" is the
 * part a second visit needs and the part nobody remembers a month later.
 */
export function FailInstallationDialog({
  deviceId,
  open,
  onClose,
}: {
  deviceId: string;
  open: boolean;
  onClose: () => void;
}) {
  const client = useApiClient();
  const { t } = useTranslation("pages");
  const queryClient = useQueryClient();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<FailFormValues>({
    resolver: zodResolver(failFormFields),
    defaultValues: { failureReason: "" },
  });

  const mutation = useMutation({
    ...failInstallationMutation(client),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["devices", "installations", deviceId] });
    },
  });

  function close() {
    reset();
    mutation.reset();
    onClose();
  }

  const onSubmit = handleSubmit(async (values) => {
    try {
      await mutation.mutateAsync({ deviceId, body: values });
      close();
    } catch {
      // Reported by the alert below.
    }
  });

  return (
    <FormDialog
      open={open}
      title={t("dialogAbandonTitle")}
      description={t("dialogAbandonDescription")}
      submitLabel={t("abandonInstallation")}
      pending={isSubmitting || mutation.isPending}
      onSubmit={(event) => void onSubmit(event)}
      onCancel={close}
    >
      <div className="flex flex-col gap-4">
        {mutation.isError ? <Alert>{mutation.error.message}</Alert> : null}

        <TextField
          label={t("fieldWhatWentWrong")}
          required
          placeholder={t("fieldWhatWentWrongPlaceholder")}
          hint={t("abandonHint")}
          {...(errors.failureReason?.message === undefined
            ? {}
            : { error: errors.failureReason.message })}
          {...register("failureReason")}
        />
      </div>
    </FormDialog>
  );
}
